import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, RefreshCw, Store } from 'lucide-react';
import {
  connectionsApi,
  spApiOauthApi,
  type SpApiConnectionType,
  type SpApiOauthConnection,
  type SpApiRegion,
  type SpApiRegionsResponse,
  type SpApiVendorLocation,
} from '@/api/client';
import { ADS_VENDOR_LINKS_QUERY_KEY } from '@/utils/adsVendorLinks';
import {
  vendorAlreadySynced,
  vendorIsEnabled,
  vendorSetupStatus,
  vendorSetupStatusClass,
  vendorSetupStatusLabel,
} from '@/utils/vendorEnrollmentStatus';
import { SpAccountAsinsTable } from '@/components/SpAccountAsinsTable';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/utils/cn';
import {
  SP_API_OAUTH_CONNECTIONS_QUERY_KEY,
  SP_API_REGIONS_QUERY_KEY,
} from '@/components/SpApiOauthConnections';

async function startSpApiOAuth(
  type: SpApiConnectionType,
  credentialId?: string | null,
  opts?: { region?: string; location?: string }
) {
  const res = credentialId
    ? await spApiOauthApi.reconnect(credentialId)
    : await spApiOauthApi.getAuthorizeUrl({
        type,
        region: opts?.region,
        location: opts?.location,
      });
  if (!res.success || !res.data?.authorize_url) {
    throw new Error('Could not start Seller/Vendor authorization');
  }
  window.location.href = res.data.authorize_url;
}

function healthLabel(health: string): string {
  switch (health) {
    case 'reauth_required':
      return 'Re-authorization required';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'inactive_risk':
      return 'At risk (inactive)';
    default:
      return 'Active';
  }
}

function healthClass(health: string): string {
  if (health === 'reauth_required') return 'text-error';
  if (health === 'expiring_soon' || health === 'inactive_risk') return 'text-warning';
  return 'text-foreground-subtle';
}

export function SpVerticalConnectionsPanel({
  type,
  preferredCredentialId,
}: {
  type: SpApiConnectionType;
  preferredCredentialId?: string | null;
}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [region, setRegion] = useState('IN');
  const [vendorLocation, setVendorLocation] = useState('IN');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const regionsQuery = useQuery({
    queryKey: SP_API_REGIONS_QUERY_KEY,
    queryFn: async (): Promise<SpApiRegionsResponse> => {
      const res = await spApiOauthApi.listRegions();
      if (!res.success) throw new Error('Failed to load regions');
      return res.data;
    },
    staleTime: 60 * 60_000,
  });

  const connectionsQuery = useQuery({
    queryKey: [...SP_API_OAUTH_CONNECTIONS_QUERY_KEY, type],
    queryFn: async () => {
      const res = await spApiOauthApi.listConnections({ type });
      if (!res.success) throw new Error('Failed to load connections');
      return res.data.connections;
    },
    refetchInterval: (query) => {
      const rows = query.state.data ?? [];
      if (type === 'vendor' && rows.some((c) => vendorSetupStatus(c) === 'syncing')) {
        return 5_000;
      }
      return 5 * 60_000;
    },
  });

  const adsVendorLinksQuery = useQuery({
    queryKey: ADS_VENDOR_LINKS_QUERY_KEY,
    queryFn: async () => {
      const res = await connectionsApi.listAdsVendorLinks();
      if (!res.success) throw new Error('Failed to load Ads links');
      return res.data;
    },
    enabled: true,
    staleTime: 30_000,
  });

  const connections = connectionsQuery.data ?? [];
  const regions = regionsQuery.data?.regions ?? [];
  const vendorLocations = regionsQuery.data?.vendor_locations ?? [];

  useEffect(() => {
    const locationFallback =
      vendorLocations.find((l) => l.code === 'IN')?.code ||
      regionsQuery.data?.default_vendor_location ||
      vendorLocations[0]?.code ||
      'IN';
    if (
      type === 'vendor' &&
      vendorLocations.length &&
      !vendorLocations.some((l) => l.code === vendorLocation)
    ) {
      setVendorLocation(locationFallback);
    }
    const regionFallback =
      regions.find((r) => r.code === 'IN')?.code ||
      regionsQuery.data?.default_region ||
      regions[0]?.code ||
      'IN';
    if (type === 'seller' && regions.length && !regions.some((r) => r.code === region)) {
      setRegion(regionFallback);
    }
  }, [regionsQuery.data, type, vendorLocation, vendorLocations, region, regions]);

  useEffect(() => {
    if (!connections.length) {
      setSelectedId(null);
      return;
    }
    if (preferredCredentialId && connections.some((c) => c.id === preferredCredentialId)) {
      setSelectedId(preferredCredentialId);
      return;
    }
    setSelectedId((prev) =>
      prev && connections.some((c) => c.id === prev) ? prev : connections[0].id
    );
  }, [connections, preferredCredentialId]);

  const selected = useMemo(
    () => connections.find((c) => c.id === selectedId) ?? null,
    [connections, selectedId]
  );

  const linkedAdsAccounts = useMemo(() => {
    if (!selected) return [];
    return (adsVendorLinksQuery.data?.profiles ?? []).filter((profile) =>
      profile.vendors.some((vendor) => vendor.credentialId === selected.id)
    );
  }, [adsVendorLinksQuery.data, selected]);

  const connectMutation = useMutation({
    mutationFn: () =>
      startSpApiOAuth(
        type,
        null,
        type === 'vendor' ? { location: vendorLocation } : { region }
      ),
    onError: (err) => {
      showToast((err as Error).message || 'Could not start OAuth', 'error');
    },
  });

  const reconnectMutation = useMutation({
    mutationFn: (credentialId: string) => startSpApiOAuth(type, credentialId),
    onError: (err) => {
      showToast((err as Error).message || 'Could not reconnect', 'error');
    },
  });

  const vendorSetupMutation = useMutation({
    mutationFn: async (credentialId: string) => {
      const res = await connectionsApi.setupAccounts({
        marketplace: 'amazon_vendor',
        externalAccountIds: [credentialId],
      });
      if (!res.success) throw new Error('Failed to start Vendor Central setup');
      return res.data;
    },
    onSuccess: async (result) => {
      const started = result.results.filter((r) => r.started).length;
      const ready = result.results.filter((r) => r.alreadyReady).length;
      if (started > 0) {
        showToast('Vendor Central setup started', 'success');
      } else if (ready > 0) {
        showToast('Vendor Central already synced and enabled', 'success');
      }
      await queryClient.invalidateQueries({
        queryKey: [...SP_API_OAUTH_CONNECTIONS_QUERY_KEY, type],
      });
    },
    onError: (err) => {
      showToast((err as Error).message || 'Could not start setup', 'error');
    },
  });

  const vendorToggleMutation = useMutation({
    mutationFn: async ({
      credentialId,
      isEnabled,
    }: {
      credentialId: string;
      isEnabled: boolean;
    }) => {
      const res = await connectionsApi.setEnabled('amazon_vendor', credentialId, isEnabled);
      if (!res.success) throw new Error('Failed to update Vendor Central connection');
      return res.data;
    },
    onSuccess: async (_data, vars) => {
      showToast(
        vars.isEnabled
          ? 'Vendor Central enabled — daily sync will continue'
          : 'Vendor Central disabled — cron will skip this connection',
        'success'
      );
      await queryClient.invalidateQueries({
        queryKey: [...SP_API_OAUTH_CONNECTIONS_QUERY_KEY, type],
      });
    },
    onError: (err) => {
      showToast((err as Error).message || 'Could not update connection', 'error');
    },
  });

  const onVendorEnabledToggle = (conn: SpApiOauthConnection) => {
    if (vendorSetupStatus(conn) === 'syncing') return;
    if (vendorAlreadySynced(conn)) {
      vendorToggleMutation.mutate({
        credentialId: conn.id,
        isEnabled: !vendorIsEnabled(conn),
      });
      return;
    }
    vendorSetupMutation.mutate(conn.id);
  };

  const vendorBusy = vendorSetupMutation.isPending || vendorToggleMutation.isPending;

  const refreshAccountsMutation = useMutation({
    mutationFn: async (credentialId: string) => {
      const res = await spApiOauthApi.refreshAccounts(credentialId);
      if (!res.success) throw new Error('Failed to refresh accounts');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...SP_API_OAUTH_CONNECTIONS_QUERY_KEY, type],
      });
      showToast('Accounts refreshed', 'success');
    },
    onError: (err) => {
      showToast((err as Error).message || 'Could not refresh accounts', 'error');
    },
  });

  const title = type === 'vendor' ? 'Vendor Central' : 'Seller Central';
  const sellerRegionOptions: SpApiRegion[] = regions.length
    ? regions
    : [
        { code: 'IN', label: 'India', marketplaces: [] },
        { code: 'NA', label: 'North America', marketplaces: [] },
        { code: 'EU', label: 'Europe', marketplaces: [] },
        { code: 'FE', label: 'Far East', marketplaces: [] },
      ];
  const vendorLocationOptions: SpApiVendorLocation[] = vendorLocations.length
    ? vendorLocations
    : [
        {
          code: 'IN',
          label: 'India',
          region: 'IN',
          consent_base: 'https://www.vendorcentral.in',
        },
      ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-foreground-muted mt-1">
            {type === 'vendor'
              ? 'Connect each Vendor Central login separately, then enable it to start the full data backfill. Cron only continues while Enabled.'
              : 'Connect Seller Central, then list marketplace participations under that selling partner.'}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {type === 'vendor' ? (
            <label className="inline-flex flex-col gap-1 text-sm text-foreground-muted">
              <span>Vendor Central location</span>
              <Select
                aria-label="Vendor Central location"
                value={vendorLocation}
                onChange={(e) => setVendorLocation(e.target.value)}
                className="py-1.5 text-sm min-w-[8rem]"
                disabled={regionsQuery.isLoading || connectMutation.isPending}
              >
                {vendorLocationOptions.map((loc) => (
                  <option key={loc.code} value={loc.code}>
                    {loc.label || loc.code} ({loc.code})
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <label className="inline-flex flex-col gap-1 text-sm text-foreground-muted">
              <span>Region</span>
              <Select
                aria-label={`${title} region`}
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="py-1.5 text-sm min-w-[8rem]"
                disabled={regionsQuery.isLoading || connectMutation.isPending}
              >
                {sellerRegionOptions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label || r.code}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <Button
            type="button"
            disabled={connectMutation.isPending}
            onClick={() => connectMutation.mutate()}
          >
            {connectMutation.isPending ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden />
                Redirecting…
              </>
            ) : (
              <>
                <Link2 className="w-3.5 h-3.5" aria-hidden />
                Connect {title}
              </>
            )}
          </Button>
        </div>
      </div>

      {connectionsQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="w-8 h-8" />
        </div>
      ) : connectionsQuery.isError ? (
        <div className="rounded-xl border border-error/20 bg-error/5 p-6 text-center">
          <p className="text-sm text-error mb-3">
            {(connectionsQuery.error as Error).message || 'Failed to load connections'}
          </p>
          <Button type="button" variant="secondary" onClick={() => void connectionsQuery.refetch()}>
            Retry
          </Button>
        </div>
      ) : connections.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-8 text-center text-foreground-muted space-y-4">
          <Store className="w-8 h-8 mx-auto text-foreground-subtle" aria-hidden />
          <p>No {title} connections yet. Connect to authorize Amazon and list accounts.</p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <ul className="space-y-2">
            {connections.map((conn: SpApiOauthConnection) => {
              const label =
                conn.display_name?.trim() ||
                `${title} · ${conn.selling_partner_id}`;
              const selectedConn = conn.id === selected?.id;
              const enabled = vendorIsEnabled(conn);
              const syncing = vendorSetupStatus(conn) === 'syncing';
              return (
                <li key={conn.id}>
                  <div
                    className={cn(
                      'rounded-xl border px-3 py-3 transition-colors',
                      selectedConn
                        ? 'border-brand bg-brand/5'
                        : 'border-border bg-surface hover:border-brand/40'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(conn.id)}
                      className="w-full text-left"
                    >
                      <p className="font-medium text-foreground truncate">{label}</p>
                      <p className="text-xs text-foreground-subtle mt-0.5 truncate">
                        {conn.region}
                        {conn.marketplace_id ? ` · ${conn.marketplace_id}` : ''}
                        {typeof conn.account_count === 'number'
                          ? ` · ${conn.account_count} account${conn.account_count === 1 ? '' : 's'}`
                          : ''}
                        {conn.created_by_email ? ` · ${conn.created_by_email}` : ''}
                      </p>
                      <p className={cn('text-xs mt-1', healthClass(conn.health))}>
                        {healthLabel(conn.health)}
                      </p>
                      {type === 'vendor' ? (
                        <p className={cn('text-xs mt-1', vendorSetupStatusClass(conn))}>
                          {vendorSetupStatusLabel(conn)}
                          {conn.last_error || conn.enrollment?.lastError
                            ? ` — ${conn.last_error || conn.enrollment?.lastError}`
                            : ''}
                        </p>
                      ) : null}
                    </button>
                    {type === 'vendor' ? (
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {vendorSetupStatus(conn) === 'failed' ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={vendorBusy}
                            onClick={() => vendorSetupMutation.mutate(conn.id)}
                          >
                            Retry setup
                          </Button>
                        ) : (
                          <span />
                        )}
                        <label className="inline-flex items-center gap-2 text-sm text-foreground-muted cursor-pointer">
                          <span>Enabled</span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={syncing || enabled}
                            disabled={vendorBusy || syncing}
                            onClick={() => onVendorEnabledToggle(conn)}
                            className={cn(
                              'relative h-6 w-11 rounded-full transition-colors',
                              syncing || enabled ? 'bg-brand' : 'bg-border'
                            )}
                          >
                            <span
                              className={cn(
                                'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-surface shadow transition-transform',
                                (syncing || enabled) && 'translate-x-5'
                              )}
                            />
                          </button>
                        </label>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          {selected ? (
            <div className="space-y-5 min-w-0">
              <div className="rounded-xl border border-border bg-surface px-4 py-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {selected.display_name || selected.selling_partner_id}
                  </p>
                  <p className="text-xs text-foreground-subtle mt-1 break-all">
                    SP ID {selected.selling_partner_id} · {selected.region}
                    {selected.marketplace_id ? ` · ${selected.marketplace_id}` : ''}
                    {selected.created_by_email ? ` · ${selected.created_by_email}` : ''}
                  </p>
                  <p className="text-xs text-foreground-muted mt-1">
                    {linkedAdsAccounts.length === 0
                      ? 'Not linked to an Ads account yet. Map it from the Ads tab.'
                      : `Linked Ads accounts: ${linkedAdsAccounts
                          .map(
                            (profile) =>
                              profile.accountName?.trim() || profile.profileId
                          )
                          .join(', ')}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {type === 'seller' ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={refreshAccountsMutation.isPending}
                      onClick={() => refreshAccountsMutation.mutate(selected.id)}
                    >
                      Refresh accounts
                    </Button>
                  ) : null}
                  {type === 'vendor' ? (
                    <>
                      {vendorSetupStatus(selected) === 'failed' ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={vendorBusy}
                          onClick={() => vendorSetupMutation.mutate(selected.id)}
                        >
                          Retry setup
                        </Button>
                      ) : null}
                      <label className="inline-flex items-center gap-2 text-sm text-foreground-muted cursor-pointer">
                        <span>Enabled</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={
                            vendorSetupStatus(selected) === 'syncing' || vendorIsEnabled(selected)
                          }
                          disabled={vendorBusy || vendorSetupStatus(selected) === 'syncing'}
                          onClick={() => onVendorEnabledToggle(selected)}
                          className={cn(
                            'relative h-6 w-11 rounded-full transition-colors',
                            vendorSetupStatus(selected) === 'syncing' || vendorIsEnabled(selected)
                              ? 'bg-brand'
                              : 'bg-border'
                          )}
                        >
                          <span
                            className={cn(
                              'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-surface shadow transition-transform',
                              (vendorSetupStatus(selected) === 'syncing' ||
                                vendorIsEnabled(selected)) &&
                                'translate-x-5'
                            )}
                          />
                        </button>
                      </label>
                    </>
                  ) : null}
                  {selected.auth_source !== 'self_authorized' ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={reconnectMutation.isPending}
                      onClick={() => reconnectMutation.mutate(selected.id)}
                    >
                      Reconnect
                    </Button>
                  ) : null}
                </div>
              </div>

              {(selected.accounts?.length ?? 0) > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    Discovered accounts
                  </h3>
                  <ul className="space-y-2">
                    {selected.accounts!.map((account) => (
                      <li
                        key={account.id}
                        className="rounded-lg border border-border bg-page px-3 py-2 text-sm"
                      >
                        <p className="font-medium text-foreground truncate">
                          {account.display_name ||
                            account.store_name ||
                            account.external_account_key}
                        </p>
                        <p className="text-xs text-foreground-subtle mt-0.5 truncate">
                          {[
                            account.country_code,
                            account.marketplace_id,
                            account.currency_code,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-foreground-muted">
                  {type === 'seller'
                    ? 'No marketplace participations discovered yet. Refresh accounts after OAuth completes.'
                    : 'Vendor account will appear after OAuth completes.'}
                </p>
              )}

              <SpAccountAsinsTable
                credentialId={selected.id}
                marketplaceId={selected.marketplace_id}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
